import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import AutocompleteInput from "../autocomplete-input";
import classNames from "classnames";
import { isValidBirthday, isValidEmail, isValidPassword, isValidPhoneNew, isValidPhoneArea, isValidPostalCode, isOnlyNumbers } from "../../utils/validations";
import { format, scrollToErrorElement } from "../../utils/dom";
import { emailFormat } from "../../utils/email";
import { getOS } from "../../utils/getOS";
import getSiteInfo from "../../utils/getSiteInfo";
import { getIdNumberRule, getMarketCountryCode } from "../../services/siteService";
import "./style.scss";
import { isEmptyObject } from "../../utils/siteUtils";

const Input = ({ pattern, label, infoMessage, warningMessage, value = "", type, onChange, onBlur, onFocus, onHasValidationError, showError, className, name, autocomplete, location, placeholder, maxLength = 100, skipValidation = false, readOnly = false, preventScroll = false, submitTimestamp }) => {
  const inputElement = useRef(null);
  const labelElement = useRef(null);
  const input = useRef();
  const [inputValue, setInputValue] = useState(value);
  const [inputType, setInputType] = useState(type);
  const [hasError, setHasError] = useState(showError);
  const [hasValidationError, setHasValidationError] = useState(false);
  const [hasFocus, setHasFocus] = useState(false);
  const [notchWidth, setNotchWidth] = useState({ width: "auto" });
  const [patternValue, setPatternValue] = useState(pattern);
  const [inputMode, setInputMode] = useState("text");
  const [datePlaceholder, setDatePlaceholder] = useState("");
  const [siteInfo, setSiteInfo] = useState(null);
  const [dateFormat, setDateFormat] = useState({});
  const [dateSeparator, setDateSeparator] = useState("/");
  const [isAutofilled, setIsAutofilled] = useState(false);
  const [isSsnRuleLoaded, setIsSsnRuleLoaded] = useState(false);
  const [ssnRule, setSsnRule] = useState(null);
  const isDisabled = useMemo(() => {
    return readOnly || (type === "dateOfBirth" && !isEmptyObject(dateFormat)) || (type === "ssn" && !isSsnRuleLoaded && !isEmptyObject(dateFormat));
  }, [readOnly, type, isSsnRuleLoaded]);
  useEffect(() => {
    switch (type) {
      case "tel":
        setInputMode("tel");
        break;
      default:
        setInputValue("text");
        break;
    }
  }, [type]);

  useEffect(() => {
    if (showError && submitTimestamp) setHasError(true);
  }, [submitTimestamp]);

  const daysInMonth = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

  const formatDateOfBirth = value => {
    if (inputValue.length > value.length && inputValue[inputValue.length - 1] === dateSeparator) {
      value = value.substr(0, value.length - 1);
    }

    if (/\D\/$/.test(inputValue)) {
      value = value.substr(0, value.length - 3);
    }

    var values = value.split(dateSeparator).map(function(v) {
      return v.replace(/\D/g, "");
    });

    const year = new Date().getUTCFullYear();
    if (values[dateFormat.year.index]) {
      values[dateFormat.year.index] = checkValue(values[dateFormat.year.index], year);
    }
    if (values[dateFormat.month.index]) {
      values[dateFormat.month.index] = checkValue(values[dateFormat.month.index], 12);
    }

    if (values[dateFormat.day.index]) {
      values[dateFormat.day.index] = checkValue(values[dateFormat.day.index], 31);
    }

    var output = values.map(function(v, i) {
      if ((i === dateFormat.year.index && v.length === dateFormat.year.length) || (i === dateFormat.month.index && v.length === dateFormat.month.length) || (i === dateFormat.day.index && v.length === dateFormat.day.length)) {
        return v + dateSeparator;
      } else {
        return v;
      }
    });
    return output.join("").substr(0, 10);
  };

  const formatCreditCard = value => {
    if (inputValue.length > value.length && inputValue[inputValue.length - 1] === "/") {
      value = value.substr(0, value.length - 1);
    }

    if (/\D\/$/.test(inputValue)) {
      value = value.substr(0, value.length - 3);
    }

    var values = value.split("/").map(function(v) {
      return v.replace(/\D/g, "");
    });

    const year = new Date().getUTCFullYear() + 10;
    if (values[0]) {
      values[0] = checkValue(values[0], 12);
    }
    if (values[1]) {
      values[1] = checkValue(values[1], year);
    }

    var output = values.map(function(v, i) {
      if (i === 0 && v.length === 2) {
        return v + "/";
      } else {
        return v;
      }
    });
    return output.join("").substr(0, 5);
  };

  const formatCreditCardNumber = value => {
    if (inputValue.length > value.length && inputValue[inputValue.length - 1] === " ") {
      value = value.substr(0, value.length - 1);
    }

    if (/\D\/$/.test(inputValue)) {
      value = value.substr(0, value.length - 3);
    }

    var values = value.split(" ").map(function(v) {
      return v.replace(/\D/g, "");
    });

    if (values[0]) {
      values[0] = checkValue(values[0], 9999);
    }
    if (values[1]) {
      values[1] = checkValue(values[1], 9999);
    }
    if (values[2]) {
      values[2] = checkValue(values[2], 9999);
    }
    if (values[3]) {
      values[3] = checkValue(values[3], 9999);
    }

    var output = values.map(function(v, i) {
      if (v.length === 4) {
        return v + " ";
      } else {
        return v;
      }
    });
    return output.join("").substr(0, 19);
  };

  const formatPhoneArea = value => {
    if (!value) {
      input.current.placeholder = "";
    }
    if (value.length === 1) {
      value = value.match(/\+/) ? `+` : isOnlyNumbers(value) ? `+${value}` : "";
    }
    if (value.length > 1 && !isValidPhoneArea(value)) {
      value = !value.match(/\+/) ? `+${value}` : value.substr(0, value.length - 1);
    }
    return value.trim();
  };

  const formatPhone = value => {
    if (!isOnlyNumbers(value)) {
      value = value ? value.substr(0, value.length - 1) : "";
    }
    return value.trim();
  };

  const checkValue = (str, max) => {
    if (str.charAt(0) !== "0" || str === "00") {
      var num = parseInt(str);
      if (isNaN(num) || num <= 0 || num > max) num = 1;
      str = num > parseInt(max.toString().charAt(0)) && num.toString().length === 1 ? "0" + num : num.toString();
    }
    return str;
  };

  const checkIsOver18 = useCallback(value => {
    if (value.length !== 10) {
      return false;
    }
    let dateOfBirthEntered = value.split("/").map(function(v) {
      return parseInt(v);
    });

    const todaysDate = new Date();

    if (todaysDate.getUTCFullYear() - 18 > dateOfBirthEntered[0]) {
      return true;
    } else if (todaysDate.getUTCFullYear() - 18 === dateOfBirthEntered[0]) {
      if (todaysDate.getUTCMonth() + 1 > dateOfBirthEntered[1]) {
        return true;
      } else if (todaysDate.getUTCMonth() + 1 === dateOfBirthEntered[1] && todaysDate.getUTCDate() >= dateOfBirthEntered[2]) {
        return true;
      } else {
        return false;
      }
    } else {
      return false;
    }
  }, []);

  const validatePattern = useCallback(() => {
    return patternValue.test(inputValue);
  }, [patternValue, inputValue]);

  const validateEmail = useCallback(isValidEmail, []);
  const validateBirthday = useCallback(isValidBirthday, []);
  const validatePhone = useCallback(isValidPhoneNew, []);
  const validatePhoneArea = useCallback(isValidPhoneArea, []);
  const validatePostalCode = useCallback(isValidPostalCode, []);
  const validateSsn = useCallback(
    value => {
      if (!value) {
        return true;
      }

      let { pattern = null } = ssnRule || {};
      let result = true;
      if (pattern) {
        let reg = new RegExp(`${pattern}`, "ig");
        result = reg.test(value);
      }
      return result;
    },
    [ssnRule]
  );
  const checkCreditCardIsValid = useCallback(() => {
    let creditCardEntered = inputValue.split("/").map(function(v) {
      return parseInt(v);
    });

    const todaysDate = new Date();
    const year = parseInt(
      todaysDate
        .getUTCFullYear()
        .toString()
        .substr(2, 2)
    );

    if (parseInt(creditCardEntered[1]) > year) {
      return true;
    } else if (parseInt(creditCardEntered[1]) === year) {
      if (parseInt(creditCardEntered[0]) >= todaysDate.getUTCMonth() + 1) {
        return true;
      } else {
        return false;
      }
    } else {
      return false;
    }
  }, [inputValue]);

  const checkValidCardNumber = useCallback(() => {
    return inputValue.length === 19;
  }, [inputValue]);

  const togglePassword = () => (inputType === "password" ? setInputType("text") : setInputType("password"));

  const checkPasswordRequirmentIsMeet = useCallback(isValidPassword, []);
  const onComponentInput = event => {
    setInputValue(event.target.value);
  };
  const onComponentFocus = event => {
    setHasValidationError(false);
    setHasError(false);
    setHasFocus(true);

    if (labelElement && inputValue === undefined) {
      setNotchWidth({
        width: labelElement.clientWidth * 0.75 + 10 + "px"
      });
    }
    if (type === "dateOfBirth" && value.length === 0) {
      input.current.placeholder = datePlaceholder;
    }
    if (type === "tel-area" && !value) {
      input.current.placeholder = "+XXX";
    }

    onFocus(event);
  };

  const onComponentBlur = event => {
    let { value } = event.target;
    if (!value) {
      setNotchWidth({ width: "auto" });
    }
    if (type === "dateOfBirth") {
      value = formatDateOfBirth(value);
    } else if (type === "creditCard") {
      value = formatCreditCard(value);
    } else if (type === "creditCardNumber") {
      value = formatCreditCardNumber(value);
    } else if (type === "tel-area") {
      value = formatPhoneArea(value);
    } else if (type === "tel") {
      value = formatPhone(value);
    }
    const { isValid, isOver18 } = checkIsValid(value);
    setHasFocus(false);
    onBlur(event, isValid, isOver18);
  };

  const birtdayMonthAndDayIsOk = (days, month) => {
    let allowedDays = daysInMonth[parseInt(month) - 1];
    let _days = parseInt(days);
    return allowedDays >= _days;
  };

  const leapYearIsOk = parts => {
    const isLeapYear = year => new Date(year, 1, 29).getDate() === 29;

    if (parts[dateFormat.day?.index].toString().trim() === "29") {
      if (isLeapYear(parts[dateFormat.year?.index])) {
        return true;
      } else {
        return false;
      }
    } else {
      return true;
    }
  };
  const getItalyMonthFromSSN = str => {
    const mStr = "ABCDEHLMPRST";
    return mStr.indexOf(str.toUpperCase()) + 1;
  };
  const checkIsValid = value => {
    let isValid = true,
      isOver18 = true;
    if (type === "email" && value) {
      let index = 0;
      let other = null;
      if (emailFormat && emailFormat.length > 0) {
        emailFormat.forEach((item) => {
          if (value.indexOf(item) > -1) {
            index = value.indexOf(item);
            other = value.replace(item, "")
          }
        });
      }
      if (index && other) {
        isValid = validateEmail(value) && index === other?.length;
      } else {
        isValid = validateEmail(value)
      }
    }

    if (type === "password" && value) {
      isValid = checkPasswordRequirmentIsMeet(value);
    }

    if (type === "dateOfBirth" && value) {
      if (dateFormat?.year) {
        const parts = value.split(dateSeparator),
          formattedValue = `${parts[dateFormat.year?.index]}/${parts[dateFormat.month?.index]}/${parts[dateFormat.day?.index]}`;
        // regex check
        isValid = validateBirthday(formattedValue);

        //month and days is ok ex 31 feb is not ok, and 29 feb ok only on leap years
        isValid = isValid && birtdayMonthAndDayIsOk(parts[dateFormat.day?.index], parts[dateFormat.month?.index]);

        let isLeapYearOk;
        if (parts[dateFormat.month?.index] === "02") {
          isLeapYearOk = leapYearIsOk(parts);
        } else {
          isLeapYearOk = true;
        }

        isValid = isValid && isLeapYearOk;

        isOver18 = isValid && checkIsOver18(formattedValue);
      } else {
        isOver18 = true;
      }
    }
    if (type === "tel-area" && value) {
      isValid = validatePhoneArea(value);
    }

    if (type === "tel" && value) {
      isValid = isValidPhoneNew(value);
    }

    if (type === "postalCode" && value) {
      isValid = validatePostalCode(value);
    }
    let birthday = null;
    if (type === "ssn" && value) {
      isValid = validateSsn(value);
      let { birthdayPattern = null } = ssnRule || {};
      if (isValid && birthdayPattern) {
        let reg = new RegExp(`${birthdayPattern}`, "g");
        const matches = reg.exec(value);
        if (matches.length > 0) {
          const str = matches[0];
          const now = new Date();
          const thisYear = now.getFullYear();
          const yearStr = str.substr(0, 2);
          let month;
          if (getMarketCountryCode() === "IT") {
            month = getItalyMonthFromSSN(str.substr(2, 1));
            if (month <= 0) {
              isValid = false;
              isOver18 = false;
              return { isValid, isOver18, birthday };
            }
          } else {
            month = Number(str.length === 5 ? str.substr(2, 1) : str.substr(2, 2));
          }
          const day = Number(str.length === 5 ? str.substr(3, 2) : str.substr(4, 2));
          let year = Number(19 + yearStr);
          if (thisYear - year >= 100) {
            year = Number(20 + yearStr);
          }
          if (isNaN(month) || isNaN(day)) {
            isValid = false;
            isOver18 = false;
            return { isValid, isOver18, birthday };
          }
          const isLeapYear = new Date(year, 1, 29).getDate() === 29;
          const isMonthAndDayOK = ([1, 3, 5, 7, 8, 10, 12].includes(month) && day <= 31) || (month === 2 && ((isLeapYear && day <= 29) || (!isLeapYear && day <= 28))) || ([4, 6, 9, 11].includes(month) && day <= 30);
          if (!isMonthAndDayOK) {
            isValid = false;
            isOver18 = false;
            return { isValid, isOver18, birthday };
          }
          birthday = format(`${year}-${month}-${day}`, siteInfo.dateFormat);
          isValid = !!birthday;
          isOver18 = isValid && checkIsOver18(`${year}/${month}/${day}`);
        }
      }
    }
    if (!type) {
      isValid = true;
    }
    return { isValid, isOver18, birthday };
  };

  const onAutoFill = event => {
    setIsAutofilled(true);
  };

  const onComponentChange = event => {
    let { value, selectionStart, selectionEnd } = event.target;

    if (type === "dateOfBirth") {
      value = formatDateOfBirth(value);
    } else if (type === "creditCard") {
      value = formatCreditCard(value);
    } else if (type === "creditCardNumber") {
      value = formatCreditCardNumber(value);
    } else if (type === "tel-area") {
      value = formatPhoneArea(value);
    } else if (type === "tel") {
      value = formatPhone(value);
    }
    const { isValid, isOver18, birthday } = checkIsValid(value);
    setInputValue(value);
    event.target.value = value;
    onChange(event, isValid, isOver18, birthday);

    if (getOS() === "iOS") {
      if (type === "dateOfBirth" || type === "tel-area") return;
      else if (type === "email" || type === "number") {
        event.target.type = "text";
        event.target.setSelectionRange(selectionStart, selectionEnd);
      } else {
        event.target.setSelectionRange(selectionStart, selectionEnd);
      }
    }
  };

  const getDateFormat = async () => {
    getSiteInfo().then(res => {
      const separatorMatch = res.dateFormat.match(/[^a-zA-Z\d\s:]/);
      setSiteInfo(res);
      if (separatorMatch.length) {
        const parts = res.dateFormat.split(separatorMatch[0]);
        setDateSeparator(separatorMatch[0]);
        res.datePlaceholder && setDatePlaceholder(res.datePlaceholder);
        res.dateFormat &&
          setDateFormat({
            year: {
              index: parts.findIndex(item => item.match(/y/i)),
              length: parts[parts.findIndex(item => item.match(/y/i))].length
            },
            month: {
              index: parts.findIndex(item => item.match(/m/i)),
              length: parts[parts.findIndex(item => item.match(/m/i))].length
            },
            day: {
              index: parts.findIndex(item => item.match(/d/i)),
              length: parts[parts.findIndex(item => item.match(/d/i))].length
            }
          });
      }
    });
  };
  const getSSNFormat = async () => {
    const rule = await getIdNumberRule();
    setSsnRule(rule);
    setIsSsnRuleLoaded(true);
  };
  useEffect(() => {
    if (type === "email") {
      setPatternValue(/^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/);
    }
    if (type === "dateOfBirth") {
      getDateFormat();
    }
    if (type === "ssn") {
      getDateFormat();
      getSSNFormat();
    }
  }, [type]);

  useEffect(() => {
    onHasValidationError(hasError);
    !preventScroll && scrollToErrorElement(inputElement.current);
  }, [hasError, onHasValidationError]);

  useEffect(() => {
    if (!hasFocus) {
      if (type === "email" && inputValue) {
        let isValid = validateEmail(inputValue);
        setHasValidationError(!isValid);
      }

      if (type === "password" && inputValue && !skipValidation) {
        let isValid = checkPasswordRequirmentIsMeet(inputValue);
        setHasValidationError(!isValid);
      }

      if (type === "dateOfBirth") {
        if (!inputValue) {
          input.current.placeholder = "";
        } else {
          let { isOver18, isValid } = checkIsValid(inputValue);
          setHasValidationError(!isOver18 || !isValid);
        }
      }

      if (type === "creditCard") {
        let isValid = checkCreditCardIsValid();
        setHasValidationError(!isValid);
      }

      if (type === "creditCardNumber") {
        let isValid = checkValidCardNumber();
        setHasValidationError(!isValid);
      }

      if (type === "tel-area" && inputValue) {
        let isValid = validatePhoneArea(inputValue);
        setHasValidationError(!isValid);
      }

      if (type === "tel" && inputValue) {
        let isValid = validatePhone(inputValue);
        setHasValidationError(!isValid);
      }
    }
  }, [checkIsOver18, checkPasswordRequirmentIsMeet, validateEmail, validatePhone, hasError, hasFocus, inputValue, patternValue, type, validatePattern, value, checkCreditCardIsValid, checkValidCardNumber, onHasValidationError]);

  useEffect(() => {
    setInputValue(value);
    input.current.value = value;
  }, [value, dateFormat]);

  useEffect(() => {
    setHasError(showError);
  }, [showError]);

  const wrapperClasses = classNames("input-wrapper", className, { "tel-area": type === "tel-area" });

  const wrapperStyle = { zIndex: hasFocus ? 11 : 10 };

  const inputGroupClasses = classNames("input-group", {
    "auto-complete": location,
    "show-warning": hasError || hasValidationError,
    focus: hasFocus,
    "has-content": (inputValue && inputValue.length > 0) || isAutofilled
  });
  const inputClasses = classNames(
    "text-field__input",
    {
      "has-content": (inputValue && inputValue.length > 0) || isAutofilled
    },
    {
      "show-warning": hasError || hasValidationError
    }
  );

  const labelClasses = classNames("floating-label", {
    "floating-above": hasFocus || (inputValue && inputValue.length > 0) || isAutofilled
  });

  const outlineLeadingClasses = classNames("notched-outline__leading", {
    focus: hasFocus,
    "show-warning": hasError || hasValidationError
  });
  const outlineNotchClasses = classNames("notched-outline__notch", {
    focus: hasFocus,
    "has-content": (inputValue && inputValue.length > 0) || isAutofilled,
    "show-warning": hasError || hasValidationError
  });
  const outlineTrailingClasses = classNames("notched-outline__trailing", {
    focus: hasFocus,
    "show-warning": hasError || hasValidationError
  });
  autocomplete = autocomplete || "new-password";

  const inputProps = {
    className: inputClasses,
    autoComplete: autocomplete,
    value: inputValue || "",
    onInput: onComponentInput,
    onChange: onComponentChange,
    onBlur: onComponentBlur,
    onFocus: onComponentFocus,
    placeholder: placeholder,
    type: inputType,
    name: name,
    maxLength: maxLength,
    inputMode: inputMode,
    readOnly: isDisabled,
    onAnimationStart: onAutoFill
  };

  return (
    <div ref={inputElement} className={wrapperClasses} style={wrapperStyle}>
      <div className={"input-row"}>
        <div className={inputGroupClasses}>
          {location ? (
            <AutocompleteInput
              inputProps={inputProps}
              ref={input}
              types={location.types}
              onAddressChanged={address => {
                location.onAutocomplete(address);
              }}
            />
          ) : (
            <input {...inputProps} autoComplete={autocomplete} ref={input} />
          )}
          {label && <div className="input-label">{label}</div>}
          {/*<div className={"notched-outline"}>*/}
          {/*  <div className={outlineLeadingClasses} />*/}
          {/*  <div className={outlineNotchClasses} style={notchWidth}>*/}
          {/*    {label && label !== "" && (*/}
          {/*      <label ref={labelElement} className={labelClasses}>*/}
          {/*        {label}*/}
          {/*      </label>*/}
          {/*    )}*/}
          {/*  </div>*/}
          {/*  <div className={outlineTrailingClasses} />*/}
          {/*</div>*/}
          {inputType === "text" && (
            <label className="password-toggle" onClick={togglePassword}>
              <svg xmlns="http://www.w3.org/2000/svg" height="24" viewBox="0 0 24 24" width="24">
                <path d="M0 0h24v24H0V0z" fill="none" />
                <path d="M12 4C7 4 2.73 7.11 1 11.5 2.73 15.89 7 19 12 19s9.27-3.11 11-7.5C21.27 7.11 17 4 12 4zm0 12.5c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" />
              </svg>
            </label>
          )}
          {inputType === "password" && (
            <label className="password-toggle" onClick={togglePassword}>
              <svg xmlns="http://www.w3.org/2000/svg" height="24" viewBox="0 0 24 24" width="24">
                <path d="M0 0h24v24H0V0zm0 0h24v24H0V0zm0 0h24v24H0V0zm0 0h24v24H0V0z" fill="none" />
                <path d="M12 6.5c2.76 0 5 2.24 5 5 0 .51-.1 1-.24 1.46l3.06 3.06c1.39-1.23 2.49-2.77 3.18-4.53C21.27 7.11 17 4 12 4c-1.27 0-2.49.2-3.64.57l2.17 2.17c.47-.14.96-.24 1.47-.24zM3.42 2.45L2.01 3.87l2.68 2.68C3.06 7.83 1.77 9.53 1 11.5 2.73 15.89 7 19 12 19c1.52 0 2.97-.3 4.31-.82l3.43 3.43 1.41-1.41L3.42 2.45zM12 16.5c-2.76 0-5-2.24-5-5 0-.77.18-1.5.49-2.14l1.57 1.57c-.03.18-.06.37-.06.57 0 1.66 1.34 3 3 3 .2 0 .38-.03.57-.07L14.14 16c-.65.32-1.37.5-2.14.5zm2.97-5.33c-.15-1.4-1.25-2.49-2.64-2.64l2.64 2.64z" />
              </svg>
            </label>
          )}
          {type !== "password" && type !== "cardNumber" && (
            <div className="icon-container">
              {(hasError || hasValidationError) && (
                <svg xmlns="http://www.w3.org/2000/svg" height="24" viewBox="0 0 24 24" width="24">
                  <path d="M0 0h24v24H0V0z" fill="none" />
                  <path d="M11 15h2v2h-2zm0-8h2v6h-2zm.99-5C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8z" />
                </svg>
              )}
            </div>
          )}
        </div>
      </div>
      {(hasError || hasValidationError) && <span className="warning-message">{warningMessage}</span>}
      {infoMessage != null && (
        <span
          className="info-message"
          dangerouslySetInnerHTML={{
            __html: infoMessage
          }}
        />
      )}
    </div>
  );
};

Input.propTypes = {
  pattern: PropTypes.string,
  infoMessage: PropTypes.string,
  warningMessage: PropTypes.string,
  value: PropTypes.string,
  type: PropTypes.oneOf(["email", "password", "dateOfBirth", "creditCard", "cardNumber", "tel", "tel-area", "postalCode", "ssn"]), // type can be: number?
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  onFocus: PropTypes.func,
  onHasValidationError: PropTypes.func,
  hasError: PropTypes.bool,
  showError: PropTypes.bool,
  className: PropTypes.string,
  name: PropTypes.string
};

Input.defaultProps = {
  value: "",
  showError: false,
  onBlur: () => {},
  onChange: () => {},
  onFocus: () => {},
  onHasValidationError: () => {},
  name: ""
};

export default Input;
